/**
 * The refund dialog. The screen this whole feature was missing.
 *
 * THE RULE IT IS BUILT AROUND: the operator is told the truth BEFORE they click, and nothing
 * is discovered afterwards. Four facts qualify, and all four are known before submission:
 *
 *   1. WHICH PAYMENT METHOD, and what that means for the money. A card refund reaches the
 *      learner in 5-10 business days. FPX has a SIXTY-DAY window, can take up to six weeks,
 *      and CAN FAIL OUTRIGHT — after which the academy has to pay the learner some other way.
 *      GrabPay is about five minutes with a ninety-day window. FPX and GrabPay cannot be
 *      disputed. All of it comes from `truth_banner`, computed on the server.
 *   2. HOW MUCH CAN BE REFUNDED, and on what basis. The basis is what was actually CHARGED,
 *      never the list price and never the quote: the quote includes the processing amount and
 *      excludes any Stripe promotion code the learner used. The arithmetic is shown, not
 *      asserted.
 *   3. WHOSE MONEY IT IS. The academy's. A refund is a debit against the academy's own Stripe
 *      balance, the platform takes no commission on the sale and cannot fund a refund, and the
 *      processing amount Stripe kept on the original payment does not come back. If the
 *      balance is short, that is said in plain words with the number and the academy's next
 *      action — never a raw Stripe error, and never a platform-funded fallback, because there
 *      is not one.
 *   4. WHAT ACCESS WILL BE REMOVED, AND WHAT WILL BE KEPT. Rendered by `GrantPlan` from the
 *      server's own plan, so a learner who bought a course separately keeps it when the path
 *      that also contained it is refunded.
 *
 * A CLOSED WINDOW DISABLES THE BUTTON. It does not submit and let Stripe manufacture the
 * refusal: the deadline is knowable now, and a refusal that arrives after the click arrives in
 * Stripe's words with the operator already committed. Same for an amount below Stripe's
 * minimum, which the payload now carries so this form does not hold a second copy of it.
 *
 * WHAT THIS DIALOG NEVER SAYS is "refunded". The server answers `status: "requested"` with
 * `state_update_mode: "webhook_async"`, and for FPX the gap between those two words is weeks.
 */

import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Badge, Banner, Button, Dialog, Field, SelectField } from '../../shared/ui';
import { formatMoney } from '../../shared/lib/pricing';
import { ReasonField, reasonProblem } from './ReasonField';
import { GrantPlan } from './GrantPlan';
import {
  CertificateChoices,
  certificateChoicesComplete,
  useOperationId,
} from './CertificateChoices';
import {
  OPS_COPY,
  explainOpsFailure,
  netAt,
  refundBasis,
  requestRefund,
  windowState,
  type WireOrderScreen,
  type WireRefundResult,
} from '../api/paymentOps';

/** A row of the arithmetic. Amounts are right-aligned tabular figures; see `payments.css`. */
function MoneyRow({
  label,
  amountMinor,
  currency,
  hint,
  emphasis,
  negative,
}: {
  label: string;
  amountMinor: number;
  currency: string;
  hint?: string;
  emphasis?: boolean;
  negative?: boolean;
}) {
  const classes = ['ops-money__row'];
  if (emphasis) classes.push('ops-money__row--total');
  if (negative) classes.push('ops-money__row--negative');
  return (
    <div className={classes.join(' ')}>
      <span>
        {label}
        {hint ? <small className="admin-muted">{hint}</small> : null}
      </span>
      <b>{formatMoney(amountMinor, currency)}</b>
    </div>
  );
}

export function RefundDialog({
  screen,
  onClose,
  onDone,
}: {
  screen: WireOrderScreen;
  onClose: () => void;
  onDone: (result: WireRefundResult) => void;
}) {
  // MINTED ONCE, WHEN THE DIALOG OPENS, and reused on every retry of this dialog. A fresh id
  // on retry defeats both the database constraint and Stripe's derived idempotency key
  // (sha256 of "thinlms:refund:" + operation_id), which is how one refund becomes two.
  //
  // Through the shared hook rather than `useState(() => newOperationId())`, because that
  // function THROWS on a browser with no `crypto.randomUUID` -- it will not hand out an id
  // that might collide -- and a throw during render takes the whole screen down. The hook
  // turns it into a refusal this dialog can render.
  const operation = useOperationId();

  const basis = refundBasis(screen);
  const window = windowState(screen.truth_banner);

  // THE ONLY DIVISION BY 100 IN THIS FILE, AND IT IS NOT FORMATTING. An amount BOX has to hold
  // a plain editable number, so the input's value is major units as digits -- `formatMoney`
  // would put a currency symbol and a thousands separator into a field the operator then has to
  // type into. Every amount DISPLAYED goes through `formatMoney`; these three sites produce
  // form values, and `amountMinor` below rounds back to an integer before anything is sent.
  const [amountInput, setAmountInput] = useState(() =>
    basis.state === 'settled' ? (basis.refundableMinor / 100).toFixed(2) : '',
  );
  const [reason, setReason] = useState('');
  const [stripeReason, setStripeReason] = useState(screen.stripe_reason_default);
  // PRE-TICKED, from the server's own default. The ordinary meaning of a refund is that the
  // sale is undone, so the operator has to untick to get the other behaviour.
  const [revokeAccess, setRevokeAccess] = useState(screen.revoke_access_default);
  const [certificateChoices, setCertificateChoices] = useState<
    Record<string, 'revoke' | 'retain'>
  >({});
  const [submitted, setSubmitted] = useState(false);

  const amountMinor = useMemo(() => {
    // Parsed from major units because that is what an operator types, and rounded ONCE here so
    // no float reaches the wire. `amount_minor` must be an integer or the server refuses it.
    const parsed = Number.parseFloat(amountInput.replace(/,/g, ''));
    if (!Number.isFinite(parsed)) return Number.NaN;
    return Math.round(parsed * 100);
  }, [amountInput]);

  const mutation = useMutation({
    mutationFn: () =>
      requestRefund(screen.order.order_ref, {
        operationId: operation.id ?? '',
        amountMinor,
        reason,
        stripeReason,
        revokeAccess,
        version: screen.version,
        certificateDecisions: certificateChoices,
      }),
    onSuccess: onDone,
  });

  /* -------- what is wrong with this form, if anything, in the operator's words ---- */

  const amountProblem = ((): string | null => {
    if (basis.state !== 'settled') return null;
    if (!Number.isFinite(amountMinor)) return 'Enter an amount.';
    if (amountMinor <= 0) return 'A refund must be for more than zero.';
    if (amountMinor > basis.refundableMinor) {
      return `Only ${formatMoney(basis.refundableMinor, basis.currency)} of the ${formatMoney(
        basis.chargedMinor,
        basis.currency,
      )} charged is still refundable.`;
    }
    // The exemption is the server's and it matters: the whole remainder is always allowed,
    // even when it is below Stripe's minimum, or a small leftover could never be returned.
    if (amountMinor < screen.minimum_refund_minor && amountMinor !== basis.refundableMinor) {
      return `Stripe cannot process a refund smaller than ${formatMoney(
        screen.minimum_refund_minor,
        basis.currency,
      )}. Refund the whole remaining ${formatMoney(
        basis.refundableMinor,
        basis.currency,
      )} instead, or nothing.`;
    }
    return null;
  })();

  const certificatesSettled =
    !revokeAccess || certificateChoicesComplete(screen.certificates, certificateChoices);

  const blocked =
    operation.id === null ||
    !screen.refund_available ||
    window.kind === 'closed' ||
    basis.state !== 'settled' ||
    amountProblem !== null ||
    reasonProblem(reason) !== null ||
    !certificatesSettled;

  const failure = mutation.isError ? explainOpsFailure(mutation.error) : null;
  const net = basis.state === 'settled' ? netAt(screen, Number.isFinite(amountMinor) ? amountMinor : 0) : null;
  const partial = basis.state === 'settled' && amountMinor > 0 && amountMinor < basis.refundableMinor;

  return (
    <Dialog
      wide
      title={`Refund ${screen.order.learner.username}`}
      onClose={onClose}
      footer={
        <>
          <Button
            variant="danger"
            busy={mutation.isPending}
            disabled={blocked}
            onClick={() => {
              setSubmitted(true);
              if (!blocked) mutation.mutate();
            }}
          >
            {revokeAccess ? 'Refund and remove access' : 'Refund and keep access'}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      {operation.problem ? <Banner tone="danger">{operation.problem}</Banner> : null}

      {/* ---- 1. the truth about this payment method, first, before any control ---- */}
      <section className="ops-truth" data-tone={screen.truth_banner.tone} aria-live="polite">
        <h4>
          {screen.truth_banner.label}
          {window.kind === 'open' && window.urgent ? (
            <Badge tone="warning">{window.daysLeft ?? '?'} days left</Badge>
          ) : null}
          {window.kind === 'closed' ? <Badge tone="danger">Window closed</Badge> : null}
          {screen.truth_banner.disputable ? null : <Badge tone="info">Cannot be disputed</Badge>}
        </h4>
        {/* Verbatim. These lines carry measured settlement times and the CAN FAIL OUTRIGHT
            warning for FPX; a local paraphrase would drop exactly the part that matters. */}
        {screen.truth_banner.lines.map((line) => (
          <p key={line}>{line}</p>
        ))}
        {window.kind === 'open' && window.deadline ? (
          <p className="admin-muted">Refundable through Stripe until {window.deadline.slice(0, 10)}.</p>
        ) : null}
        <p className="admin-muted">Source: {screen.truth_banner.source}</p>
      </section>

      {/* A closed window, or any other reason the server says a refund is not available, is
          stated in the server's own sentence and the button above stays visibly unavailable.
          Note that removing access without refunding is often still possible — that control
          lives on the order screen, and this dialog says so rather than implying a dead end. */}
      {!screen.refund_available || window.kind === 'closed' ? (
        <Banner tone="danger">
          {screen.refund_unavailable_reason ||
            'Stripe will not accept a refund on this payment any more.'}{' '}
          Removing this learner’s access without refunding may still be available on the order
          screen.
        </Banner>
      ) : null}

      {basis.state !== 'settled' ? (
        <Banner tone="warning">
          No payment ever settled on this order, so there is nothing to refund. That is not the
          same as a refund of nothing.
        </Banner>
      ) : (
        <>
          {/* ---- 2. the arithmetic, shown rather than asserted ---- */}
          <div className="ops-money">
            <MoneyRow label="Course price" amountMinor={basis.listMinor} currency={basis.currency} />
            <MoneyRow
              label="Payment processing"
              hint="added to the learner’s price so this academy is not out of pocket on the sale"
              amountMinor={basis.processingMinor}
              currency={basis.currency}
            />
            {basis.discountMinor > 0 ? (
              <MoneyRow
                label="Promotion code applied at checkout"
                hint="Stripe reduced the amount after the price was quoted"
                amountMinor={-basis.discountMinor}
                currency={basis.currency}
              />
            ) : null}
            <MoneyRow
              label="Actually charged"
              amountMinor={basis.chargedMinor}
              currency={basis.currency}
              emphasis
              hint="the only basis a refund is calculated on"
            />
            {basis.alreadyRefundedMinor > 0 ? (
              <MoneyRow
                label="Already refunded"
                amountMinor={basis.alreadyRefundedMinor}
                currency={basis.currency}
              />
            ) : null}
            {basis.pendingMinor > 0 ? (
              <MoneyRow
                label="Refund already in flight"
                hint="counted against the remainder, because it is money already committed"
                amountMinor={basis.pendingMinor}
                currency={basis.currency}
              />
            ) : null}
            <MoneyRow
              label="Still refundable"
              amountMinor={basis.refundableMinor}
              currency={basis.currency}
              emphasis
            />
          </div>

          <Field
            name="refund-amount"
            label={`Amount to refund (${basis.currency})`}
            inputMode="decimal"
            value={amountInput}
            onChange={(event) => setAmountInput(event.target.value)}
            hint={`Up to ${formatMoney(basis.refundableMinor, basis.currency)}. Minimum ${formatMoney(
              screen.minimum_refund_minor,
              basis.currency,
            )} unless you refund the whole remainder.`}
            error={submitted && amountProblem ? amountProblem : undefined}
          />

          <div className="ops-presets">
            <Button
              variant="ghost"
              onClick={() => setAmountInput((basis.refundableMinor / 100).toFixed(2))}
            >
              Refund everything still refundable
            </Button>
            {/* The labelled ex-processing preset. Offered because the academy genuinely cannot
                recover Stripe's fee on the original payment, and this is the only refund shape
                in which it does not lose money — labelled rather than left as a bare number,
                because an unexplained smaller amount reads as a mistake. */}
            {screen.ex_fee_preset.amount_minor > 0 &&
            screen.ex_fee_preset.amount_minor !== basis.refundableMinor ? (
              <Button
                variant="ghost"
                onClick={() =>
                  setAmountInput((screen.ex_fee_preset.amount_minor / 100).toFixed(2))
                }
                title={screen.ex_fee_preset.explanation}
              >
                {screen.ex_fee_preset.label} (
                {formatMoney(screen.ex_fee_preset.amount_minor, basis.currency)})
              </Button>
            ) : null}
          </div>

          {/* ---- 3. whose money this is ---- */}
          {net ? (
            <div className="ops-money">
              <MoneyRow
                label="This academy received"
                hint="after the processing Stripe kept on the original payment (estimated)"
                amountMinor={net.chargedMinor - net.feeRetainedMinor}
                currency={basis.currency}
              />
              <MoneyRow
                label="This refund"
                amountMinor={net.refundMinor}
                currency={basis.currency}
              />
              <MoneyRow
                label="Left with this academy"
                amountMinor={net.academyNetMinor}
                currency={basis.currency}
                emphasis
                negative={net.academyNetMinor < 0}
                hint={
                  net.academyNetMinor < 0 ? 'this refund costs more than the sale brought in' : undefined
                }
              />
            </div>
          ) : null}
          <p className="admin-muted">{screen.fee_is_not_returned}</p>
          <p className="admin-muted">{OPS_COPY.moneyIsTheAcademys}</p>
        </>
      )}

      {/* ---- 4. what happens to access ---- */}
      <label className="ops-check">
        <input
          type="checkbox"
          checked={revokeAccess}
          onChange={(event) => setRevokeAccess(event.target.checked)}
        />
        <span>
          Remove this learner’s access
          <small className="admin-muted">{OPS_COPY.revokeDefault}</small>
        </span>
      </label>

      {revokeAccess ? (
        <>
          {partial ? <Banner tone="warning">{screen.partial_refund_note}</Banner> : null}
          <GrantPlan decisions={screen.grant_decisions} />
          {screen.certificates.length > 0 ? (
            <CertificateChoices
              offers={screen.certificates}
              value={certificateChoices}
              onChange={setCertificateChoices}
            />
          ) : null}
        </>
      ) : (
        <Banner tone="info">
          The learner keeps everything this purchase granted. This is recorded as a separate
          action from a refund that removes access, so the audit trail says which one you chose.
        </Banner>
      )}

      <SelectField
        name="stripe-reason"
        label="Stripe’s own reason code (optional)"
        value={stripeReason}
        onChange={(event) => setStripeReason(event.target.value)}
        options={[
          ['', 'Not stated'],
          ...screen.stripe_reasons.map((row) => [row.value, row.label] as [string, string]),
        ]}
      />
      {/* Marking a payment fraudulent changes Stripe Radar's behaviour for this card and email
          against this academy for good. It is not the default, and the consequence is printed
          rather than left in Stripe's documentation. */}
      {screen.stripe_reasons
        .filter((row) => row.value === stripeReason && row.consequence)
        .map((row) => (
          <Banner key={row.value} tone="warning">
            {row.consequence}
          </Banner>
        ))}

      <ReasonField value={reason} onChange={setReason} showProblem={submitted} />
      <p className="admin-muted">{OPS_COPY.auditIsPermanent}</p>

      {failure ? (
        <Banner tone={failure.outcome === 'already_done' ? 'info' : 'danger'}>
          {failure.message}
          {failure.nextStep ? (
            <>
              <br />
              {failure.nextStep}
            </>
          ) : null}
        </Banner>
      ) : null}
    </Dialog>
  );
}
